import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { rbacService, ADMIN_EMAIL } from '../services/rbacService';
import type { UserRole, RolePermissions } from '../types/rbac';

export function useRBAC() {
  const { user, loading: authLoading } = useAuth();
  const [role, setRole] = useState<UserRole>(() => rbacService.getRole(user?.email));
  const [permissions, setPermissions] = useState<RolePermissions>(() =>
    rbacService.getPermissions(user?.email)
  );

  useEffect(() => {
    // Sync immediately when user changes
    const currentRole = rbacService.getRole(user?.email);
    const currentPerms = rbacService.getPermissions(user?.email);
    setRole(currentRole);
    setPermissions(currentPerms);

    // Subscribe to external RBAC updates
    const unsubscribe = rbacService.subscribe(() => {
      setRole(rbacService.getRole(user?.email));
      setPermissions(rbacService.getPermissions(user?.email));
    });

    return unsubscribe;
  }, [user]);

  const isAdmin = user?.email === ADMIN_EMAIL || role === 'admin';
  const isEditor = isAdmin || role === 'editor';
  const isModerator = isAdmin || role === 'moderator';

  const hasPermission = (permission: keyof RolePermissions): boolean => {
    if (isAdmin) return true;
    return Boolean(permissions[permission]);
  };

  const hasRole = (targetRole: UserRole | UserRole[]): boolean => {
    if (isAdmin) return true;
    const targetRoles = Array.isArray(targetRole) ? targetRole : [targetRole];
    return targetRoles.includes(role);
  };

  return {
    user,
    role,
    permissions,
    isAdmin,
    isEditor,
    isModerator,
    hasPermission,
    can: hasPermission,
    hasRole,
    loading: authLoading,
  };
}
